import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Paper,
  Stack,
  Divider,
  Chip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { motion } from "framer-motion";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SaveIcon from "@mui/icons-material/Save";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import imgicon from "../../Components/Assets/images/imageicon.jpg";
import { toast } from "react-hot-toast";
import LoaderBackdrop from "../../Components/Loader/LoaderBackdrop";

// Small currency formatter used throughout this view
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const InventoryTransformation = ({ isCreate, setIsCreate, isChanged, setIsChanged }) => {
  const [items, setItems] = useState([]);
  const [bomItems, setBomItems] = useState([]);
  const [bomComponents, setBomComponents] = useState([]);
  const [units, setUnits] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState(null);
  const [rejectionReasonsList, setRejectionReasonsList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [assignedStaffId, setAssignedStaffId] = useState(null);
  const { company_id,user_id } = useSelector((state) => state.GlobalVariables);
const [loading,setLoading] = useState(false)
  const [inputItems, setInputItems] = useState([
    {
      itemName: "",
      quantity: "",
      unitPrice: "",
      unit: "",
      associatedItems: [],
      itemtotalprice: "",
    },
  ]);

  const [outputItems, setOutputItems] = useState([
    {
      productName: "",
      quantity: "",
      costPrice: "",
      unit: "",
      isTaxInclusive: true,
      newcostprice: "",
    },
  ]);

  const [imagePreview, setImagePreview] = useState(imgicon);
  const [imageBase64, setImageBase64] = useState("");
  const [totalInputCost, setTotalInputCost] = useState(0);
  const [rejectedTotal, setRejectedTotal] = useState("");
  const rejectionRowInitial = { quantity: "", rejected_reason_id: null };
  const [rejectionRows, setRejectionRows] = useState([rejectionRowInitial]);
  const fileInputRef = useRef(null);

  const allocatedRejected = rejectionRows.reduce(
    (sum, r) => sum + (parseFloat(r.quantity) || 0),
    0
  );
  const rejectedNum = parseFloat(rejectedTotal) || 0;

  const updateRejectionRow = (idx, field, value) => {
    setRejectionRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
  };

  const addRejectionRow = () => {
    setRejectionRows((prev) => [...prev, { ...rejectionRowInitial }]);
  };

  const removeRejectionRow = (idx) => {
    setRejectionRows((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
    );
  };
  
  const fetchBomItemList = async () => {
    try {
      const dataToSend = {
        company_id,
        search_by: null,
        search: null,
        per_page: null,
        page: null,
      };
      const res = await configServ.getBomItemList(dataToSend);
      if (res.status === 200 && Array.isArray(res.data)) {
        setBomItems(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchItemList = async () => {
    try {
      const dataToSend = {
        company_id,
        search_by: null,
        search: null,
        per_page: null,
        page: null,
      };
      const res = await configServ.getItemList(dataToSend);
      if (res.status === 200 && res.data.length > 0) {
        setItems(res.data);
        const unitSet = new Set(
          res.data.map((item) => item.unit).filter(Boolean)
        );
        setUnits([...unitSet]);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchActiveShifts = async () => {
    try {
      const res = await configServ.getActiveShifts({ company_id });
      if (res.status === 200 && Array.isArray(res.data)) {
        setShifts(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchActiveRejectionReasons = async () => {
    try {
      const res = await configServ.getActiveRejectionReasons();
      if (res.status === 200 && Array.isArray(res.data)) {
        setRejectionReasonsList(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchActiveStaff = async () => {
    try {
      const res = await configServ.getActiveStaff({ company_id });
      if (res.status === 200 && Array.isArray(res.data)) {
        setStaffList(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchBomAssociatedItems = async (bom_item_id) => {
    try {
      const res = await configServ.getBomAssociatedItems({ bom_item_id });
      if (res.status === 200 && Array.isArray(res.data)) {
        setBomComponents(res.data);
      } else {
        setBomComponents([]);
      }
    } catch (e) {
      console.log(e);
      setBomComponents([]);
    }
  };

  // Scale source items whenever BOM components or manufactured quantity changes
  const manufacturedQty = outputItems[0]?.quantity;
  useEffect(() => {
    if (bomComponents.length === 0) return;
    const M = parseFloat(manufacturedQty) || 0;
    const scaled = bomComponents.map((c) => {
      const baseGross = parseFloat(c.gross_qty) || 0;
      const baseNet = parseFloat(c.qty) || 0;
      const unitPrice = parseFloat(c.costprice) || 0;
      const quantity = baseGross * M;
      const netQuantity = baseNet * M;
      return {
        item_id: c.item_id,
        itemName: c.item_name,
        itemcode: c.item_code,
        unit: c.unit || "",
        unitPrice,
        quantity,
        netQuantity,
        itemtotalprice: quantity * unitPrice,
        associatedItems: [],
      };
    });
    setInputItems(scaled);
    calculateTotalInputCost(scaled);
  }, [bomComponents, manufacturedQty]);

  useEffect(() => {
    if (isCreate) {
      fetchBomItemList();
      fetchItemList();
      fetchActiveShifts();
      fetchActiveRejectionReasons();
      fetchActiveStaff();
    }
  }, [isCreate]);

  // Auto-fill the manufactured product's unit cost as totalInputCost / quantity
  // whenever the total or the manufactured quantity changes. Manual edits stay
  // until the next recompute.
  useEffect(() => {
    setOutputItems((prev) => {
      if (!prev[0]?.product_id) return prev;
      const qty = parseFloat(prev[0].quantity) || 0;
      const unitCost =
        qty > 0
          ? Math.round((Number(totalInputCost || 0) / qty) * 100) / 100
          : 0;
      if (Number(prev[0].newcostprice || 0) === unitCost) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], newcostprice: unitCost };
      return updated;
    });
  }, [totalInputCost, manufacturedQty]);

  const handleInputChange = (index, field, value) => {
    const updatedItems = [...inputItems];
    const numberFields = ["quantity"];
    updatedItems[index][field] = numberFields.includes(field)
      ? value === ""
        ? ""
        : Number(value)
      : value;
    // Recalculate itemtotalprice if quantity or unitPrice changes
    const quantity =
      field === "quantity" ? value : updatedItems[index].quantity;
    const unitPrice =
      field === "unitPrice" ? value : updatedItems[index].unitPrice;
    updatedItems[index].itemtotalprice = quantity * unitPrice || 0;
    setInputItems(updatedItems);
    calculateTotalInputCost(updatedItems);
  };

  const handleAssociatedItemChange = (mainIndex, aIndex, field, value) => {
    const updated = [...inputItems];
    updated[mainIndex].associatedItems[aIndex][field] = value;
    setInputItems(updated);
  };

  const handleItemSelect = async (index, selectedItem) => {
    if (!selectedItem) return;

    const updatedItems = [...inputItems];

    // base item data
    updatedItems[index] = {
      ...updatedItems[index],
      item_id: selectedItem.id,
      itemName: selectedItem.item_name,
      unitPrice: selectedItem.costprice,
      quantity: 1,
      unit: selectedItem.unit || "",
      associatedItems: [],
      itemtotalprice: selectedItem.costprice * 1, // default qty = 1
    };

    // if (selectedItem.isItemAssociated === 1) {
    //   const associated = await fetchAssociatedItem(selectedItem.id);

    //   // 🛠 Map each associated item to include full item data from `items`
    //   const enrichedAssociated = associated.map((aItem) => {
    //     const matchedItem = items.find((itm) => itm.id === aItem.item_id);

    //     return {
    //       ...aItem,
    //       item_name: matchedItem?.item_name || aItem.item_name,
    //       unitPrice: matchedItem?.rate_unit || "",
    //       unit: matchedItem?.unit || "",
    //     };
    //   });

    //   updatedItems[index].associatedItems = enrichedAssociated;
    // }

    setInputItems(updatedItems);
    calculateTotalInputCost(updatedItems);
  };

  const fetchAssociatedItem = async (itemId) => {
    try {
      const response = await configServ.getAssociateItem({
        associatedWith_id: itemId,
      });
      return response;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  const handleAddItem = () => {
    setInputItems([
      ...inputItems,
      {
        itemName: "",
        quantity: "",
        unitPrice: "",
        unit: "",
        associatedItems: [],
        itemtotalprice: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = inputItems.filter((_, i) => i !== index);
    setInputItems(updatedItems);
    calculateTotalInputCost(updatedItems);
  };

  const handleOutputChange = (index, field, value) => {
    const updated = [...outputItems];

    // Convert only certain fields to number
    const numberFields = ["quantity", "newcostprice", "costPrice"];

    updated[index][field] = numberFields.includes(field)
      ? value === ""
        ? ""
        : Number(value)
      : value;

    setOutputItems(updated);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageBase64(reader.result);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleReset = () => {
    setInputItems([
      {
        itemName: "",
        quantity: "",
        unitPrice: "",
        unit: "",
        itemtotalprice: "",
      },
    ]);
    setOutputItems([
      {
        productName: "",
        quantity: "",
        costPrice: "",
        unit: "",
        isTaxInclusive: true,
        newcostprice: "",
      },
    ]);
    setImageBase64("");
    setImagePreview(imgicon);
    setShiftId(null);
    setAssignedStaffId(null);
    setBomComponents([]);
    setRejectedTotal("");
    setRejectionRows([{ ...rejectionRowInitial }]);
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    setTotalInputCost(0);
  };

  const handleSubmit = async () => {
    // Validate shift
    if (!shiftId) {
      toast.error("Please select a shift.");
      return;
    }

    // Validate inputItems one by one
    for (let i = 0; i < inputItems.length; i++) {
      const item = inputItems[i];
      if (!item.item_id) {
        toast.error(`Source Items ${i + 1}: Item not selected`);
        return;
      }
      if (!item.itemName) {
        toast.error(`Source Items ${i + 1}: Item name is missing`);
        return;
      }
      if (!item.unit) {
        toast.error(`Source Items ${i + 1}: Unit is missing`);
        return;
      }
      if (!item.quantity) {
        toast.error(`Source Items ${i + 1}: Quantity is missing`);
        return;
      }
    }

    // Validate output item
    const output = outputItems[0];
    if (!output.product_id) {
      toast.error("Manufactured Product: Product not selected");
      return;
    }
    if (!output.productName) {
      toast.error("Manufactured Product: Product name is missing");
      return;
    }
    if (!output.unit) {
      toast.error("Manufactured Product: Unit is missing");
      return;
    }
    if (!output.quantity) {
      toast.error("Manufactured Product: Quantity is missing");
      return;
    }
    if (!assignedStaffId) {
      toast.error("Manufactured Product: Please select an Assigned staff");
      return;
    }
    if (!shiftId) {
      toast.error("Manufactured Product: Please select a Shift");
      return;
    }    
    if (output.costPrice < 0 || output.costPrice === "" || output.costPrice === null || output.costPrice === undefined) {
      toast.error("Manufactured Product: Previous cost price is missing");
      return;
    }
    if (output.newcostprice === "" || output.newcostprice === null || output.newcostprice === undefined) {
      toast.error("Manufactured Product: Rate is missing");
      return;
    }

    // Validate rejected products allocation
    if (rejectedNum > 0) {
      const manufacturedQtyNum = parseFloat(output.quantity) || 0;
      if (rejectedNum > manufacturedQtyNum) {
        toast.error(
          `Rejected products (${rejectedNum}) cannot exceed manufactured quantity (${manufacturedQtyNum}).`
        );
        return;
      }
      if (allocatedRejected !== rejectedNum) {
        toast.error(
          `Rejected quantity and reasons must must be selected.`
        );
        return;
      }
      for (let i = 0; i < rejectionRows.length; i++) {
        const r = rejectionRows[i];
        const qty = parseFloat(r.quantity) || 0;
        if (qty > 0 && !r.rejected_reason_id) {
          toast.error(`Rejection row ${i + 1}: reason is required.`);
          return;
        }
      }
    }

    // Build outputs with shift_id + rejected_products; build manufactured_products_rejections list
    const outputsWithRejected = outputItems.map((o, idx) =>
      idx === 0
        ? {
            ...o,
            shift_id: shiftId,
            assigned_to: assignedStaffId,
            rejected_products: rejectedNum,
          }
        : o
    );
    const manufactured_products_rejections =
      rejectedNum > 0
        ? rejectionRows
            .filter((r) => (parseFloat(r.quantity) || 0) > 0)
            .map((r) => ({
              manufactured_product_id: outputItems[0].product_id,
              quantity: parseFloat(r.quantity) || 0,
              rejected_reason_id: r.rejected_reason_id,
            }))
        : [];

    // All validations passed — send data
    const payload = {
      company_id: company_id,
      inputs: inputItems,
      outputs: outputsWithRejected,
      manufactured_products_rejections,
      totalInputCost: totalInputCost,
      imageBase64: imageBase64,
      created_by:user_id,
    };
    console.log("payload",payload)

    try {
      setLoading(true)
      const result = await configServ.storeManufacturedProduct(payload);
      if(result.success){
        toast.success("Data saved successfully!");
        handleReset();
        setIsCreate(!isCreate)
        if (setIsChanged) setIsChanged(!isChanged);
      }
      // Optional: reset form here
    } catch (e) {
      console.log("Error saving:", e);
      toast.error("Something went wrong while saving data.");
    }finally{
      setLoading(false)
    }
  };

  const calculateTotalInputCost = (items) => {
    const total = items.reduce(
      (sum, item) => sum + (parseFloat(item.itemtotalprice) || 0),
      0
    );
    setTotalInputCost(total);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{
        width: "100%",
        backgroundColor: "#e3f3fc",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      <LoaderBackdrop open={loading} />
      <Box>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                background: "#fff",
                border: "1px solid #e0e6ed",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 2, py: 1.25, background: "#f1f8fc" }}
              >
                <Inventory2Icon fontSize="small" sx={{ color: "#1e61b4" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: "#1e61b4" }}
                >
                  Manufactured Product
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {outputItems[0]?.product_id && (
                  <Chip
                    size="small"
                    label={`Qty: ${outputItems[0].quantity || 0} ${
                      outputItems[0].unit || ""
                    }`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Divider />
              <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={bomItems}
                    getOptionLabel={(option) =>
                      `${option.item_name} (${option.item_code})`
                    }
                    onChange={(e, selectedItem) => {
                      if (!selectedItem) {
                        const cleared = [...outputItems];
                        cleared[0] = {
                          productName: "",
                          quantity: "",
                          costPrice: "",
                          unit: "",
                          isTaxInclusive: outputItems[0].isTaxInclusive,
                          newcostprice: "",
                        };
                        setOutputItems(cleared);
                        setBomComponents([]);
                        setInputItems([
                          {
                            itemName: "",
                            quantity: "",
                            unitPrice: "",
                            unit: "",
                            associatedItems: [],
                            itemtotalprice: "",
                          },
                        ]);
                        setTotalInputCost(0);
                        setRejectedTotal("");
                        setRejectionRows([{ ...rejectionRowInitial }]);
                        setAssignedStaffId(null);
                        return;
                      }
                      const updated = [...outputItems];
                      updated[0] = {
                        ...updated[0],
                        product_id: selectedItem.id,
                        productName: selectedItem.item_name,
                        costPrice: selectedItem.costprice,
                        unit: selectedItem.unit || "",
                        quantity: 1,
                        newcostprice: "",
                      };
                      setOutputItems(updated);
                      fetchBomAssociatedItems(selectedItem.id);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product Name"
                        size="small"
                      />
                    )}
                    value={
                      bomItems.find(
                        (x) => x.item_name === outputItems[0].productName
                      ) || null
                    }
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={staffList}
                    getOptionLabel={(option) =>
                      option
                        ? option.contact_name ||
                          [option.fname, option.lname]
                            .filter(Boolean)
                            .join(" ") ||
                          ""
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                    value={
                      staffList.find((s) => s.id === assignedStaffId) || null
                    }
                    onChange={(e, newValue) =>
                      setAssignedStaffId(newValue?.id || null)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Assigned" size="small" />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Autocomplete
                    options={shifts}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.shift_name} (${option.shift_code})`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                    value={shifts.find((s) => s.id === shiftId) || null}
                    onChange={(e, newValue) =>
                      setShiftId(newValue?.id || null)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Shift" size="small" />
                    )}
                  />
                </Grid>

                <Grid item xs={6} md={2}>
                  <TextField
                    label={
                      outputItems[0].unit
                        ? `${outputItems[0].unit}`
                        : "Quantity"
                    }
                    fullWidth
                    type="number"
                    size="small"
                    value={outputItems[0].quantity}
                    onChange={(e) =>
                      handleOutputChange(0, "quantity", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={6} md={2}>
                  <TextField
                    label="Unit Cost"
                    fullWidth
                    type="number"
                    size="small"
                    value={outputItems[0].newcostprice}
                    onChange={(e) =>
                      handleOutputChange(0, "newcostprice", e.target.value)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon fontSize="inherit" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* <Grid item xs={6} md={2} display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={outputItems[0].isTaxInclusive}
                        onChange={(e) =>
                          handleOutputChange(
                            0,
                            "isTaxInclusive",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Inclusive Tax"
                  />
                </Grid> */}
              </Grid>
              </Box>
            </Paper>
          </Grid>

          {outputItems[0]?.product_id && (
          <Grid item xs={12} md={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                background: "#fff",
                border: "1px solid #e0e6ed",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 2, py: 1.25, background: "#f1f8fc" }}
              >
                <PrecisionManufacturingIcon
                  fontSize="small"
                  sx={{ color: "#1e61b4" }}
                />
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: "#1e61b4" }}
                >
                  Source Items
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {inputItems.length} component
                  {inputItems.length === 1 ? "" : "s"}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                  size="small"
                  icon={<CurrencyRupeeIcon />}
                  label={`Total: ${fmt(totalInputCost)}`}
                  color="success"
                  variant="filled"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "#fafbfc" }}>
                      <TableCell sx={{ fontWeight: 700, width: 48 }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Gross Qty
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Net Qty
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Unit Cost
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Line Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inputItems.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(even)": { background: "#fafbfc" },
                          "&:hover": { background: "#f1f8fc" },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.itemName || "—"}
                          </Typography>
                          {item.itemcode && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {item.itemcode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {Number(item.quantity || 0)}
                          {item.unit && (
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ color: "text.secondary", ml: 0.5 }}
                            >
                              {item.unit}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {Number(item.netQuantity || 0)}
                          {item.unit && (
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ color: "text.secondary", ml: 0.5 }}
                            >
                              {item.unit}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          ₹{fmt(item.unitPrice)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, color: "#1e61b4" }}
                        >
                          ₹{fmt(item.quantity * item.unitPrice)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* keep the legacy fragment open so the existing comment block below stays balanced */}
              <Box sx={{ display: "none" }}>
                {inputItems.map((item, index) => (
                  <React.Fragment key={index}>

                  {/* {item.associatedItems?.length > 0 && (
                    <Grid container spacing={2} pl={3} mb={2}>
                      {item.associatedItems.map((aItem, aIndex) => (
                        <Grid container spacing={2} key={aIndex} sx={{ mt: 1 }}>
                          <Grid item xs={6} md={4}>
                            <TextField
                              label="Associated Item"
                              fullWidth
                              size="small"
                              value={aItem.item_name}
                              onChange={(e) =>
                                handleAssociatedItemChange(
                                  index,
                                  aIndex,
                                  "item_name",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={3} md={2}>
                            <TextField
                              label={aItem.unit || "Quantity"}
                              fullWidth
                              size="small"
                              type="number"
                              value={aItem.qty || ""}
                              onChange={(e) =>
                                handleAssociatedItemChange(
                                  index,
                                  aIndex,
                                  "qty",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              label="Unit Price"
                              fullWidth
                              size="small"
                              type="number"
                              value={aItem.unitPrice || ""}
                              onChange={(e) =>
                                handleAssociatedItemChange(
                                  index,
                                  aIndex,
                                  "unitPrice",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )} */}
                </React.Fragment>
                ))}
              </Box>
            </Paper>
          </Grid>
          )}

          {outputItems[0]?.product_id && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  background: "#fff",
                  border: "1px solid #e0e6ed",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 2, py: 1.25, background: "#fff7e6" }}
                >
                  <ReportProblemOutlinedIcon
                    fontSize="small"
                    sx={{ color: "#b26a00" }}
                  />
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: "#b26a00" }}
                  >
                    Rejected Manufactured Products
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {rejectedNum > 0 && (
                    <Chip
                      size="small"
                      label={`Allocated: ${allocatedRejected} / ${rejectedNum}`}
                      color={
                        allocatedRejected === rejectedNum
                          ? "success"
                          : allocatedRejected > rejectedNum
                          ? "error"
                          : "warning"
                      }
                      variant="filled"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Stack>
                <Divider />
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="No. of Rejected Products"
                        type="number"
                        fullWidth
                        size="small"
                        value={rejectedTotal}
                        onChange={(e) => setRejectedTotal(e.target.value)}
                        inputProps={{
                          min: 0,
                          max: parseFloat(outputItems[0]?.quantity) || 0,
                        }}
                        error={
                          rejectedNum >
                          (parseFloat(outputItems[0]?.quantity) || 0)
                        }
                        helperText={
                          rejectedNum >
                          (parseFloat(outputItems[0]?.quantity) || 0)
                            ? `Cannot exceed manufactured quantity (${
                                parseFloat(outputItems[0]?.quantity) || 0
                              })`
                            : `Max: ${
                                parseFloat(outputItems[0]?.quantity) || 0
                              }`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Distribute the rejected quantity across one or more
                        rows with a reason for each.
                      </Typography>
                    </Grid>
                  </Grid>

                  {rejectedNum > 0 && (
                    <Box mt={2}>
                      {rejectionRows.map((row, idx) => (
                        <Grid
                          container
                          spacing={2}
                          key={idx}
                          mb={1}
                          alignItems="center"
                        >
                          <Grid item xs={6} md={2}>
                            <TextField
                              label="Quantity"
                              type="number"
                              fullWidth
                              size="small"
                              value={row.quantity}
                              onChange={(e) =>
                                updateRejectionRow(
                                  idx,
                                  "quantity",
                                  e.target.value
                                )
                              }
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <Autocomplete
                              size="small"
                              options={rejectionReasonsList}
                              getOptionLabel={(option) =>
                                option?.reasons || ""
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.id === value?.id
                              }
                              value={
                                rejectionReasonsList.find(
                                  (r) => r.id === row.rejected_reason_id
                                ) || null
                              }
                              onChange={(e, newValue) =>
                                updateRejectionRow(
                                  idx,
                                  "rejected_reason_id",
                                  newValue?.id || null
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Rejection Reason"
                                  size="small"
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => removeRejectionRow(idx)}
                                disabled={rejectionRows.length === 1}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              {idx === rejectionRows.length - 1 && (
                                <IconButton
                                  sx={{ color: "green" }}
                                  size="small"
                                  onClick={addRejectionRow}
                                >
                                  <AddCircleIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                background: "#fff",
                border: "1px dashed #b8c4cf",
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#1e61b4" }}
              >
                Upload Image
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
              {imageBase64 ? (
                <Chip
                  size="small"
                  label="Image selected"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  PNG / JPG, max ~2&nbsp;MB recommended
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: "cover",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                  }}
                />
              )}
            </Paper>
          </Grid>
        </Grid>

        <Box
          mt={3}
          display="flex"
          justifyContent="flex-end"
          gap={1.5}
        >
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RotateLeftIcon />}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
};

export default InventoryTransformation;
